import React from 'react';
import { X, Shield, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100/50 rounded-lg text-blue-600">
                            <Shield className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Política de Privacidad y Cookies</h2>
                            <p className="text-sm text-gray-500">Bezalel Hardware Technology S.A.S.</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8">
                    <div className="prose prose-blue max-w-none text-gray-600 space-y-8">

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">1</span>
                                Objetivo
                            </h3>
                            <p>
                                Bezalel Hardware Technology S.A.S., identificada con NIT 9016638023, en cumplimiento de la Ley 1581 de 2012,
                                el Decreto 1377 de 2013 y demás normas complementarias, adopta la presente Política de Privacidad y Cookies,
                                con el propósito de garantizar el adecuado tratamiento de los datos personales de sus empleados, clientes,
                                proveedores, aliados, contratistas y demás terceros que suministren información personal a la empresa.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">2</span>
                                Alcance
                            </h3>
                            <p>
                                La presente política aplica a todas las bases de datos, sistemas, aplicaciones, sitios web y archivos físicos o
                                digitales que contengan información personal en poder de Bezalel Hardware Technology S.A.S., con domicilio digital
                                en www.bezalel.com.co.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">3</span>
                                Definiciones
                            </h3>
                            <p>
                                De acuerdo con la legislación colombiana, se entenderá por autorización, dato personal, dato sensible, encargado
                                del tratamiento, responsable del tratamiento, titular y tratamiento conforme a la Ley 1581 de 2012.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">4</span>
                                Tratamiento de la Información Personal
                            </h3>
                            <p>
                                Bezalel Hardware Technology S.A.S. recopila, almacena, usa, transmite, transfiere, actualiza y suprime datos personales,
                                incluyendo datos sensibles, de personas naturales con quienes mantiene o ha mantenido una relación comercial, laboral o contractual.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">5</span>
                                Finalidades del Tratamiento
                            </h3>
                            <p>
                                Los datos se utilizan para gestionar relaciones contractuales, atender solicitudes, enviar comunicaciones, cumplir
                                obligaciones legales, realizar estudios de mercado y garantizar la seguridad física y digital de las operaciones.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">6</span>
                                Derechos de los Titulares
                            </h3>
                            <p>
                                Los titulares pueden conocer, actualizar y rectificar sus datos personales; solicitar prueba de la autorización;
                                ser informados sobre el uso de sus datos; presentar quejas ante la Superintendencia de Industria y Comercio;
                                revocar la autorización o solicitar la supresión del dato; y acceder gratuitamente a sus datos.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">7</span>
                                Procedimiento para Ejercicio de Derechos
                            </h3>
                            <p>
                                Los titulares podrán ejercer sus derechos enviando solicitud al correo <strong>gerencia@bezalel.com.co</strong> o comunicándose
                                al teléfono <strong>+57 316 249 6551</strong>. Las solicitudes serán atendidas conforme a los plazos legales.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">8</span>
                                Seguridad de la Información
                            </h3>
                            <p>
                                Bezalel Hardware Technology S.A.S. aplica medidas administrativas, técnicas y físicas necesarias para proteger la
                                información personal contra pérdida, acceso no autorizado, alteración o destrucción.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">9</span>
                                Uso de Cookies
                            </h3>
                            <p>
                                Bezalel Hardware Technology S.A.S. utiliza cookies propias y de terceros en su sitio web www.bezalel.com.co para
                                mejorar la experiencia del usuario, analizar hábitos de navegación y ofrecer servicios personalizados.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">10</span>
                                Autorización del Titular
                            </h3>
                            <p>
                                Al suministrar información personal a Bezalel Hardware Technology S.A.S., el titular declara que ha leído, comprendido
                                y aceptado esta Política de Privacidad y autoriza de manera libre, expresa e informada a la empresa para tratar sus
                                datos personales conforme a las finalidades descritas.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">11</span>
                                Vigencia
                            </h3>
                            <p>
                                Esta política entra en vigencia el 19 de octubre de 2025 y permanecerá vigente mientras Bezalel Hardware Technology S.A.S. realice tratamiento de datos personales.
                            </p>
                        </section>

                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
                    <Button variant="outline" onClick={onClose} className="text-gray-700 border-gray-300 hover:bg-gray-100">
                        Cerrar
                    </Button>
                    <a
                        href="/legal/Politica_Privacidad_Cookies.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Button className="bg-blue-600 text-white hover:bg-blue-700">
                            <FileText className="w-4 h-4 mr-2" />
                            Ver PDF Original
                        </Button>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyModal;
